import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { of, Observable, BehaviorSubject } from 'rxjs'
import { catchError, mapTo, tap } from 'rxjs/operators'
import { User } from '../../models/user.model'
import { TokenResponse } from '../../models/token-response.model'

const apiUrl = '/api/auth/'

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public static readonly JWT_TOKEN = 'JWT_TOKEN'
    public static readonly REFRESH_TOKEN = 'REFRESH_TOKEN'
    private currentUser: User
    decodedToken: any

    currentToken: string
    currentRefreshToken: string
    currentUser$: BehaviorSubject<User> = new BehaviorSubject(null)
    constructor(private http: HttpClient) {
        this.loadFromLocalStorage()
        if (this.currentToken) {
            if (this.isTokenExpired()) {
                this.currentToken = null
                this.removeTokens()
                this.currentUser$.next(null)
                return
            }
            console.log('Recreate user from token')
        } else {
            console.log('User not logged in or expired')
        }
    }

    loadFromLocalStorage() {
        this.currentToken = localStorage.getItem(AuthService.JWT_TOKEN)
        this.currentRefreshToken = localStorage.getItem(AuthService.REFRESH_TOKEN)
        if (this.currentToken) {
            this.createUserFromToken()
        }
    }

    getJwtToken() {
        return this.currentToken
    }
    hasRoleAssigned(roles: string[]): boolean {
        // console.log(`match roles ${roles} against ${this.currentUser.roles}`)
        if (!roles) {
            return true
        }

        if (!this.currentUser) {
            return false
        }

        return (
            this.currentUser.roles.filter(value => roles.map(i => i.toUpperCase()).includes(value.toUpperCase()))
                .length > 0
        )
    }

    login(user: { username: string; password: string }): Observable<boolean> {
        return this.http.post<any>(`${apiUrl}/login`, user).pipe(
            tap(tokens => this.doLoginUser(user.username, tokens)),
            mapTo(true)
        )
    }

    logout() {
        return this.http
            .post<any>(`${apiUrl}/logout`, {
                refreshToken: this.currentRefreshToken,
            })
            .pipe(
                tap(() => this.doLogoutUser()),
                mapTo(true)
            )
    }

    isLoggedIn() {
        return !!this.currentToken
    }

    refreshToken() {
        return this.http
            .post<any>(`${apiUrl}/refresh`, {
                refreshToken: this.currentRefreshToken,
            })
            .pipe(
                tap((tokens: TokenResponse) => {
                    this.storeJwtToken(tokens.token)
                })
            )
    }

    private doLoginUser(username: string, tokens: TokenResponse) {
        this.currentToken = tokens.token
        this.currentRefreshToken = tokens.refreshToken
        this.createUserFromToken()
        this.storeTokens(tokens)
    }

    private createUserFromToken() {
        this.decodedToken = JSON.parse(atob(this.currentToken.split('.')[1]))
        this.currentUser = new User()
        this.currentUser.locale = this.decodedToken.locale
        this.currentUser.name = this.decodedToken.name
        this.currentUser.username = this.decodedToken.email
        this.currentUser.roles = this.decodedToken.groups
        this.currentUser.customerId = this.decodedToken.customer_id
        this.currentUser$.next(this.currentUser)
    }

    private doLogoutUser() {
        this.currentToken = null
        this.currentRefreshToken = null
        this.currentUser = null
        this.removeTokens()
    }

    private storeJwtToken(jwt: string) {
        localStorage.setItem(AuthService.JWT_TOKEN, jwt)
        this.decodedToken = atob(jwt.split('.')[1])
        console.log(this.decodedToken) // token
    }

    private storeTokens(tokens: TokenResponse) {
        this.storeJwtToken(tokens.token)
        localStorage.setItem(AuthService.REFRESH_TOKEN, tokens.refreshToken)
    }

    private removeTokens() {
        localStorage.removeItem(AuthService.JWT_TOKEN)
        localStorage.removeItem(AuthService.REFRESH_TOKEN)
    }

    private isTokenExpired(): boolean {
        return this.decodedToken.exp * 1000 < new Date().getTime()
    }

    public getCurrentUser() {
        return this.currentUser
    }
}
